import {
  Badge,
  Button,
  Group,
  Paper,
  Radio,
  Select,
  Stack,
  Text,
  Title,
  Loader,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { ChartCard } from '@/components/ChartCard';
import { UnifyLineChart } from '@/components/charts';
import { CHART_PALETTE_ORDER } from '@/lib/chartPalette';
import {
  IconArrowLeft,
  IconCalendar,
  IconCalendarEvent,
  IconClock,
  IconPlayerPlay,
  IconPlus,
  IconUser,
} from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { notify } from '@/lib/notify';
import type { Questionnaire, QuestionnaireItem } from '@medplum/fhirtypes';
import type { Assessment, AssessmentDataPoint } from '../../clients/data/types';
import { saveInSessionAssessment } from '../lib/saveInSessionAssessment';
import { practitionerPreferredReference } from '@/lib/loadPractitionerPreferredLabels';
import { useResolvedTemplates } from '../../settings/template-library/hooks';
import { getItemWeight } from '../../settings/template-library/utils/scoringUtils';
import {
  parseScoringConfigurationJson,
  resolveMaxScoreForQuestionnaire,
} from '../../../lib/assessmentMedplumAdapters';
import { waitForTaskForQr } from '@/lib/clientAssignmentTasks';
import { saveClientAssignmentQr } from '@/lib/clientAssignmentQr';
import { dateInputValueToDateOnly } from '@/lib/dateHelpers';
import { CLIENT_ASSESSMENT_OCCURRENCE_TASK_IDENTIFIER_SYSTEM } from '@unify/fhir-model/constants';
import {
  sessionSidebarComboboxProps,
  sessionSidebarPopoverProps,
} from '../lib/sessionSidebarComboboxProps';

interface AssessmentsTabProps {
  assessments: Assessment[];
  trends: AssessmentDataPoint[];
  patientId?: string;
  episodeId?: string;
  appointmentId?: string;
  encounterId?: string;
  onSaved?: () => Promise<void>;
}

const ASSIGN_DELIVERY = 'Assign';
const TAKE_NOW_DELIVERY = 'Take Now';

const DELIVERY_SELECT_DATA = [
  { value: ASSIGN_DELIVERY, label: 'Assign to client' },
  { value: TAKE_NOW_DELIVERY, label: TAKE_NOW_DELIVERY },
];

function severityColor(severity?: string): string {
  if (!severity) return 'gray';
  const s = severity.toLowerCase();
  if (s.includes('severe') || s.includes('threshold')) return 'red';
  if (s.includes('moderate')) return 'orange';
  if (s.includes('mild')) return 'orange';
  return 'green';
}

function scorableItems(q: Questionnaire): QuestionnaireItem[] {
  return (q.item ?? []).filter(
    (it) => (it.type === 'choice' || it.type === 'open-choice') && it.answerOption?.length,
  );
}

interface AnswerOption {
  code: string;
  display: string;
  weight: number;
}

function itemAnswerOptions(item: QuestionnaireItem): AnswerOption[] {
  return (item.answerOption ?? []).map((opt, idx) => ({
    code: opt.valueCoding?.code ?? String(idx),
    display: opt.valueCoding?.display ?? `Option ${idx}`,
    // Prefer FHIR itemWeight, then option valueInteger (common on measures), then ordinal.
    weight: getItemWeight(opt) ?? opt.valueInteger ?? idx,
  }));
}

/** Build chart series from whatever instrument keys are present on trend rows. */
export function buildTrendSeriesFromData(
  trends: AssessmentDataPoint[],
): { name: string; color: string }[] {
  const present = new Set<string>();
  for (const row of trends) {
    for (const [key, value] of Object.entries(row as unknown as Record<string, unknown>)) {
      if (key === 'date' || key === 'session') continue;
      if (typeof value === 'number' && Number.isFinite(value)) {
        present.add(key);
      }
    }
  }
  return [...present].sort().map((name, idx) => ({
    name,
    color: CHART_PALETTE_ORDER[idx % CHART_PALETTE_ORDER.length],
  }));
}

export function AssessmentsTab({
  assessments,
  trends,
  patientId,
  episodeId,
  appointmentId,
  encounterId,
  onSaved,
}: AssessmentsTabProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const {
    templates: resolvedAssessmentTemplates,
    loading: templatesLoading,
    error: templatesError,
  } = useResolvedTemplates({
    target: 'assessment',
    category: 'measures',
  });
  const templates = resolvedAssessmentTemplates;
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'browse' | 'assign' | 'take-now'>('browse');
  const [questionnaireId, setQuestionnaireId] = useState('');
  const [delivery, setDelivery] = useState<string>(ASSIGN_DELIVERY);
  const [dueDate, setDueDate] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [takeNowQuestionnaireId, setTakeNowQuestionnaireId] = useState<string | null>(null);

  const takeNowTemplate = useMemo(
    () => templates.find((q) => q.id === takeNowQuestionnaireId),
    [templates, takeNowQuestionnaireId],
  );

  const items = useMemo(
    () => (takeNowTemplate ? scorableItems(takeNowTemplate) : []),
    [takeNowTemplate],
  );

  const maxScore = useMemo(
    () => (takeNowTemplate ? resolveMaxScoreForQuestionnaire(takeNowTemplate) : 0),
    [takeNowTemplate],
  );

  const totalScore = useMemo(
    () => Object.values(answers).reduce((sum, v) => sum + v, 0),
    [answers],
  );

  const scoringConfig = useMemo(
    () => parseScoringConfigurationJson(takeNowTemplate),
    [takeNowTemplate],
  );

  const severityLabel = useMemo(() => {
    if (!scoringConfig?.outputs?.length || !scoringConfig.bands) return null;
    const firstId = scoringConfig.outputs[0].id;
    const bands = scoringConfig.bands[firstId];
    if (!bands?.length) return null;
    const sortedBands = [...bands].sort((a, b) => a.low - b.low);
    const match = sortedBands.find((b) => totalScore >= b.low && totalScore <= b.high);
    if (!match) return null;
    const idx = sortedBands.indexOf(match);
    const colors = ['green', 'blue', 'yellow', 'orange', 'red'];
    return { label: match.label, color: colors[Math.min(idx, colors.length - 1)] };
  }, [scoringConfig, totalScore]);

  const allAnswered = items.length > 0 && Object.keys(answers).length === items.length;

  const resetTakeNow = useCallback(() => {
    setAnswers({});
    setSubmitted(false);
    setTakeNowQuestionnaireId(null);
    setMode('browse');
  }, []);

  const openAssign = useCallback(() => {
    setQuestionnaireId('');
    setDelivery(ASSIGN_DELIVERY);
    setDueDate('');
    setMode('assign');
  }, []);

  const selectData = useMemo(
    () =>
      templates.map((q) => ({
        value: q.id!,
        label: q.title ?? q.name ?? q.id!,
      })),
    [templates],
  );

  const templateLabel = takeNowTemplate?.title ?? takeNowTemplate?.name ?? 'Assessment';
  const sorted = [...assessments].sort((a, b) => b.date.localeCompare(a.date));
  const trendSeries = useMemo(() => buildTrendSeriesFromData(trends), [trends]);
  const isTakeNow = delivery === TAKE_NOW_DELIVERY;

  const startTakeNow = useCallback(
    async (selectedQ: Questionnaire) => {
      if (!selectedQ.id || !patientId || !episodeId?.trim()) return;

      // ENG-3764 D8: Take Now creates no Task; saveInSessionAssessment writes the completed QR.
      setAssigning(true);
      try {
        setAnswers({});
        setSubmitted(false);
        setTakeNowQuestionnaireId(selectedQ.id);
        setMode('take-now');
      } catch (error) {
        notify({
          title: 'Could not start assessment',
          error: error,
          type: 'error',
        });
      } finally {
        setAssigning(false);
      }
    },
    [patientId, episodeId],
  );

  const handleAssign = useCallback(async () => {
    const selectedQ = templates.find((q) => q.id === questionnaireId);
    if (!selectedQ?.id) {
      notify({
        title: 'Assessment required',
        message: 'Select an active Measures questionnaire template.',
        type: 'error',
      });
      return;
    }
    if (!patientId) {
      notify({
        title: 'Patient required',
        message: 'No patient is linked to this session.',
        type: 'error',
      });
      return;
    }
    if (!episodeId?.trim()) {
      notify({
        title: 'Episode of Care required',
        message: 'This session has no Episode of Care to scope the assignment.',
        type: 'error',
      });
      return;
    }

    if (delivery === TAKE_NOW_DELIVERY) {
      await startTakeNow(selectedQ);
      return;
    }

    // ENG-3764 D1/D2: save the client's pending measures QR; the task-events listener creates the one-time Task.
    setAssigning(true);
    try {
      const qr = await saveClientAssignmentQr(medplum, {
        patientId,
        questionnaire: selectedQ,
        category: 'measures',
        author: profile?.resourceType === 'Practitioner' && profile.id ? { reference: `Practitioner/${profile.id}` } : undefined,
        episodeId,
        due: dueDate.trim() || undefined,
      });
      const outcome = await waitForTaskForQr(medplum, qr.id!, CLIENT_ASSESSMENT_OCCURRENCE_TASK_IDENTIFIER_SYSTEM);
      if (outcome.kind !== 'created') {
        await onSaved?.();
        notify({
          title: outcome.kind === 'error' ? 'Assignment failed' : outcome.kind === 'already-assigned' ? 'Already assigned' : 'Assessment saved',
          // eslint-disable-next-line no-restricted-syntax -- authored outcome text from clientAssignmentTasks, not a caught error
          message: outcome.message,
          type: outcome.kind === 'error' ? 'error' : 'warning',
        });
        setMode('browse');
        return;
      }
      await onSaved?.();
      notify({
        title: 'Assessment assigned',
        message: 'Assessment assigned to client.',
        type: 'success',
      });
      setMode('browse');
    } catch (error) {
      notify({
        title: 'Assignment failed',
        error: error,
        type: 'error',
      });
    } finally {
      setAssigning(false);
    }
  }, [
    templates,
    questionnaireId,
    patientId,
    episodeId,
    delivery,
    dueDate,
    profile,
    medplum,
    onSaved,
    startTakeNow,
  ]);

  if (mode === 'assign') {
    return (
      <Stack gap="md">
        <Group gap="xs">
          <Button
            variant="subtle"
            color="gray"
            size="compact-sm"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => setMode('browse')}
          >
            Back
          </Button>
        </Group>
        <Text size="sm" fw={600}>Assign assessment</Text>
        <Text size="xs" c="dimmed">
          Assign a scored measure to this client for the current episode, or take it now in-session.
        </Text>
        {templatesLoading ? (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="xs" c="dimmed">Loading assessment templates…</Text>
          </Group>
        ) : null}
        {templatesError ? (
          <Text size="xs" c="red">{templatesError}</Text>
        ) : null}
        <Select
          label="Assessment type"
          placeholder={
            templatesLoading
              ? 'Loading…'
              : templates.length > 0
                ? 'Select a scored measure…'
                : 'No measures templates available'
          }
          data={selectData}
          value={questionnaireId || null}
          onChange={(v) => setQuestionnaireId(v ?? '')}
          searchable
          required
          disabled={templatesLoading || selectData.length === 0}
          comboboxProps={sessionSidebarComboboxProps()}
        />
        <Select
          label="Delivery"
          data={DELIVERY_SELECT_DATA}
          value={delivery}
          onChange={(v) => setDelivery(v ?? ASSIGN_DELIVERY)}
          allowDeselect={false}
          comboboxProps={sessionSidebarComboboxProps()}
        />
        {!isTakeNow ? (
          <DatePickerInput
            label="Due date (optional)"
            placeholder="Optional"
            value={dueDate ? new Date(dueDate + 'T00:00:00') : null}
            onChange={(v) => setDueDate(dateInputValueToDateOnly(v))}
            leftSection={<IconCalendarEvent size={16} />}
            minDate={new Date()}
            clearable
            popoverProps={sessionSidebarPopoverProps()}
          />
        ) : null}
        {isTakeNow ? (
          <Text size="xs" c="dimmed">
            Take Now opens this measure in the sidebar for you to complete during the session.
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            Scoped to the current episode{episodeId ? '' : ' (unavailable)'}.
          </Text>
        )}
        <Button
          color="gold"
          loading={assigning}
          disabled={!patientId || !episodeId || !questionnaireId || templatesLoading}
          onClick={() => {
            void handleAssign();
          }}
        >
          {isTakeNow ? 'Take now' : 'Assign assessment'}
        </Button>
      </Stack>
    );
  }

  if (mode === 'take-now' && takeNowTemplate) {
    return (
      <Stack gap="md">
        <Group gap="xs">
          <Button
            variant="subtle"
            color="gray"
            size="compact-sm"
            leftSection={<IconArrowLeft size={14} />}
            onClick={resetTakeNow}
          >
            Back
          </Button>
        </Group>

        {!submitted && items.length > 0 && (
          <Stack gap="sm">
            <Group gap="xs">
              <IconPlayerPlay size={14} />
              <Text size="sm" fw={500}>{templateLabel}</Text>
              <Text size="xs" c="dimmed">({items.length} items, max {maxScore})</Text>
            </Group>

            {items.map((item, idx) => {
              const opts = itemAnswerOptions(item);
              return (
                <Paper key={item.linkId} withBorder p="sm" radius="sm">
                  <Stack gap="xs">
                    <Text size="xs" fw={500}>{idx + 1}. {item.text}</Text>
                    <Radio.Group
                      value={answers[item.linkId]?.toString() ?? ''}
                      onChange={(v) =>
                        setAnswers((prev) => ({ ...prev, [item.linkId]: parseFloat(v) }))
                      }
                    >
                      <Group gap="md" mt={4}>
                        {opts.map((opt) => (
                          <Radio
                            key={opt.code}
                            value={opt.weight.toString()}
                            label={opt.display}
                            size="xs"
                          />
                        ))}
                      </Group>
                    </Radio.Group>
                  </Stack>
                </Paper>
              );
            })}

            {allAnswered && (
              <Paper withBorder p="sm" radius="sm" bg="gray.0">
                <Group justify="space-between">
                  <Stack gap={0}>
                    <Text size="sm" fw={600}>Score: {totalScore}/{maxScore}</Text>
                    {severityLabel && (
                      <Badge size="sm" variant="light" color={severityLabel.color}>
                        {severityLabel.label}
                      </Badge>
                    )}
                  </Stack>
                  <Button
                    size="sm"
                    color="gold"
                    loading={saving}
                    disabled={!patientId || !takeNowTemplate}
                    onClick={() => {
                      if (!patientId || !takeNowTemplate) return;
                      setSaving(true);
                      const author = profile?.resourceType === 'Practitioner'
                        ? practitionerPreferredReference(profile)
                        : undefined;
                      void saveInSessionAssessment({
                        medplum,
                        questionnaire: takeNowTemplate,
                        patientId,
                        episodeId,
                        appointmentId,
                        encounterRef: encounterId ? `Encounter/${encounterId}` : undefined,
                        answers,
                        author,
                      })
                        .then(async () => {
                          setSubmitted(true);
                          await onSaved?.();
                          notify({
                            type: 'success',
                            title: 'Assessment saved',
                            message: `${templateLabel} was recorded for this episode.`,
                          });
                        })
                        .catch((err: unknown) => {
                          notify({
                            type: 'error',
                            title: 'Save failed',
                            error: err,
                            message: 'Could not save assessment.',
                          });
                        })
                        .finally(() => setSaving(false));
                    }}
                  >
                    Save Result
                  </Button>
                </Group>
              </Paper>
            )}
          </Stack>
        )}

        {submitted && (
          <Paper withBorder p="md" radius="sm" bg="green.0">
            <Stack gap="xs" align="center">
              <Title order={5}>Assessment Saved</Title>
              <Text size="sm">{templateLabel}: {totalScore}/{maxScore}</Text>
              {severityLabel && (
                <Badge variant="light" color={severityLabel.color}>{severityLabel.label}</Badge>
              )}
              <Button size="xs" variant="subtle" onClick={resetTakeNow}>
                Done
              </Button>
            </Stack>
          </Paper>
        )}

        {!submitted && items.length === 0 && (
          <Text size="sm" c="dimmed">
            This questionnaire has no scorable items.
          </Text>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <ChartCard title="Score Trends">
        {trendSeries.length === 0 ? (
          <Text size="sm" c="dimmed" py="md" ta="center">
            No scored assessment trends yet.
          </Text>
        ) : (
          <UnifyLineChart
            h={Math.max(220, 160 + trendSeries.length * 18)}
            data={trends}
            dataKey="date"
            series={trendSeries}
            withDots
            withLegend
            legendProps={{ verticalAlign: 'bottom', height: Math.max(48, 28 + trendSeries.length * 16) }}
            curveType="monotone"
            connectNulls
            dotProps={{ r: 4, strokeWidth: 0 }}
            xAxisProps={{
              tickFormatter: (v: string) => {
                const d = new Date(v + 'T00:00:00');
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              },
              tick: { fontSize: 11 },
            }}
          />
        )}
      </ChartCard>

      <Stack gap="xs">
        <Group justify="space-between" align="center" wrap="wrap" gap="xs">
          <Group gap={6}>
            <IconClock size={14} color="var(--mantine-color-dimmed)" />
            <Text size="sm" fw={600}>History</Text>
          </Group>
          <Button
            size="compact-sm"
            variant="light"
            color="gold"
            leftSection={<IconPlus size={14} />}
            disabled={!patientId}
            onClick={openAssign}
          >
            Assign assessment
          </Button>
        </Group>
        {sorted.length === 0 && (
          <Text size="sm" c="dimmed">No completed assessments yet.</Text>
        )}
        {sorted.slice(0, 8).map((a) => (
          <Paper
            key={a.id}
            withBorder
            p="sm"
            radius="sm"
            style={{ borderColor: 'var(--mantine-color-gray-3)' }}
          >
            <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
              <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" fw={600} truncate>{a.assessmentType}</Text>
                <Group gap={4} wrap="nowrap">
                  <IconCalendar size={12} color="var(--mantine-color-dimmed)" />
                  <Text size="xs" c="dimmed">{a.date}</Text>
                </Group>
                {a.provider ? (
                  <Group gap={4} wrap="nowrap">
                    <IconUser size={12} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed" truncate>{a.provider}</Text>
                  </Group>
                ) : null}
              </Stack>
              <Stack gap={4} align="flex-end" style={{ flexShrink: 0 }}>
                <Text size="sm" fw={700}>
                  {a.score}/{a.maxScore ?? '—'}
                </Text>
                {a.severity ? (
                  <Badge size="xs" variant="light" color={severityColor(a.severity)}>
                    {a.severity}
                  </Badge>
                ) : null}
              </Stack>
            </Group>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}
